import type { SelectProps } from '@mantine/core';
import { Select, Textarea } from '@mantine/core';
import { IconBolt, IconBrain } from '@tabler/icons-react';
import { cn } from '~/common/string/classname';
import { type AIActionModel, zAIActionModel } from '~/server/logic/steps/operation/types';

type SelectOptionProps = {
  label: string;
  icon: React.ElementType;
  description: string;
};

// Model options
const MODEL_OPTIONS: Record<AIActionModel, SelectOptionProps> = {
  STABLY_FLASH: {
    label: 'Stably Flash',
    icon: IconBolt,
    description: 'Fast and recommended for simple actions',
  },
  STABLY_DEEP_THINK: {
    label: 'Stably Deep Think',
    icon: IconBrain,
    description: 'Recommended for complex actions',
  },
};

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
  const modelOption = MODEL_OPTIONS[option.value as AIActionModel];
  return (
    <div>
      <div className="flex items-center gap-2">
        <modelOption.icon size={14} />
        <span className="text-xs">{option.label}</span>
      </div>
      <span className="text-[12px] text-gray-500">{modelOption.description}</span>
    </div>
  );
};

const LABEL_STYLE = 'text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';

export function AIActionConfig({
  prompt,
  model,
  onPromptChange,
  onModelChange,
  className,
  textareaProps,
  readonly,
}: {
  prompt: string;
  model: AIActionModel;
  onPromptChange: (value: string) => void;
  onModelChange: (value: AIActionModel) => void;
  className?: string;
  promptTitle?: string;
  textareaProps?: React.ComponentProps<typeof Textarea>;
  readonly?: boolean;
}) {
  const selectedModelOption = MODEL_OPTIONS[model];

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <Textarea
        label="Action Prompt"
        placeholder="e.g. Finish the onboarding flow"
        autosize
        minRows={2}
        value={prompt}
        onChange={(e) => onPromptChange(e.currentTarget.value)}
        // Submit on Enter
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const { form } = e.currentTarget;
            if (form) {
              form.requestSubmit();
            }
          }
        }}
        readOnly={readonly}
        classNames={{ label: LABEL_STYLE }}
        {...textareaProps}
      />

      <Select
        label="Model"
        data={Object.entries(MODEL_OPTIONS).map(([key, option]) => ({
          value: key,
          label: option.label,
        }))}
        value={model}
        disabled={readonly}
        leftSection={<selectedModelOption.icon size={14} />}
        comboboxProps={{ withinPortal: false }}
        renderOption={renderSelectOption}
        classNames={{ label: LABEL_STYLE }}
        allowDeselect={false}
        onOptionSubmit={(value) => onModelChange(zAIActionModel.parse(value))}
      />
    </div>
  );
}
